/**
 * AnimatedProgressBar.tsx - Animated Progress Bar Component
 * 
 * This component provides a smooth animated progress bar for loading states,
 * particularly useful during AI content generation and image creation processes.
 * 
 * Key Features:
 * - Smooth CSS animations with customizable duration
 * - Multiple animation styles (fill, pulse, gradient)
 * - Customizable colors and styling
 * - Optional text overlay with progress percentage
 * - Responsive design with mobile support
 * 
 * Usage:
 * - AI fundraiser content generation (20-30 seconds)
 * - Image generation processes (20-40 seconds)
 * - File upload and processing
 * - Any long-running async operations
 * 
 * Props:
 * - isVisible: boolean - Controls visibility
 * - message: string - Text to display above progress bar
 * - duration: number - Animation duration in seconds (default: 30)
 * - style: 'fill' | 'pulse' | 'gradient' - Animation style
 * 
 * Dependencies: React, styled-components
 */

import React, { useEffect, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';

interface AnimatedProgressBarProps {
  isVisible: boolean;
  message?: string;
  duration?: number;
  style?: 'fill' | 'pulse' | 'gradient';
  onComplete?: () => void;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fillAnimation = keyframes`
  from {
    width: 0%;
  }
  to {
    width: 100%;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const shimmerAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const ProgressContainer = styled.div<{ isVisible: boolean }>`
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin: 24px 0;
  animation: ${fadeIn} 0.3s ease-out;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
`;

const ProgressMessage = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 24px;
  text-align: center;
  line-height: 1.4;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  margin-bottom: 16px;
`;

const ProgressBarFill = styled.div<{ 
  duration: number; 
  animationStyle: 'fill' | 'pulse' | 'gradient' 
}>`
  height: 100%;
  border-radius: 4px;
  position: relative;
  overflow: hidden;

  ${props => props.animationStyle === 'fill' && css`
    background: linear-gradient(90deg, #3b82f6, #1d4ed8);
    animation: ${fillAnimation} ${props.duration}s ease-out forwards;
  `}

  ${props => props.animationStyle === 'pulse' && css`
    width: 100%;
    background: linear-gradient(90deg, #3b82f6, #1d4ed8);
    animation: ${pulseAnimation} 2s ease-in-out infinite;
  `}

  ${props => props.animationStyle === 'gradient' && css`
    width: 100%;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4, #3b82f6);
    background-size: 200% 200%;
    animation: ${gradientAnimation} 3s ease-in-out infinite;
  `}

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.4),
      transparent
    );
    animation: ${shimmerAnimation} 2s ease-in-out infinite;
  }
`;

const ProgressText = styled.div`
  font-size: 14px;
  color: #64748b;
  text-align: center;
  font-weight: 500;
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 12px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
`;

export const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({
  isVisible,
  message = "Generating your fundraiser content...",
  duration = 30,
  style = 'fill',
  onComplete
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isVisible) {
      setProgress(0);
      return;
    }

    const interval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / (duration * 10)); // Update every 100ms
        if (newProgress >= 100) {
          clearInterval(interval);
          if (onComplete) {
            setTimeout(onComplete, 500); // Small delay before calling onComplete
          }
          return 100;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [isVisible, duration, onComplete]);

  if (!isVisible) return null;

  return (
    <ProgressContainer isVisible={isVisible}>
      <ProgressMessage>{message}</ProgressMessage>
      
      <ProgressBarContainer>
        <ProgressBarFill 
          duration={duration} 
          animationStyle={style}
        />
      </ProgressBarContainer>
      
      <ProgressText>
        {style === 'fill' ? `${Math.round(progress)}% complete` : 'Processing...'}
      </ProgressText>

      <SpinnerContainer>
        <Spinner />
        <span style={{ fontSize: '14px', color: '#64748b' }}>
          This may take up to {duration} seconds
        </span>
      </SpinnerContainer>
    </ProgressContainer>
  );
};